/** @jsxImportSource @emotion/react */
"use client";

import type { ImageProps } from "@packages/framework";
import { Image } from "@packages/framework";
import * as RadixUIAvatar from "@radix-ui/react-avatar";
import type { ComponentProps } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  numericPercentages,
  numericPixels,
  radius,
} from "../../../../../global/emotion";
import { emotionDynamics } from "../../../../lib/emotionDynamics";
import { Skeleton } from "../Skeleton/Skeleton.emotion";

const baseStyle = {
  borderRadius: radius.full,
  display: "flex",
  flexShrink: 0,
};
const selectedStyle = (theme: ThemeType) => ({
  outlineColor: theme.semanticColors.primary,
  outlineOffset: numericPixels[1],
  outlineStyle: "solid",
  outlineWidth: numericPixels[2],
});

const baseImageStyle = {
  borderRadius: radius.full,
  height: numericPercentages[100],
  objectFit: "cover" as "cover",
  width: numericPercentages[100],
};

const baseFallbackStyle = (theme: ThemeType) => ({
  backgroundColor: theme.semanticColors.neutralsLower,
  borderRadius: radius.full,
  height: numericPercentages[100],
  width: numericPercentages[100],
});

export type AvatarProps = ComponentProps<typeof RadixUIAvatar.Root> & {
  imgSrc?: ImageProps["src"];
  loading?: boolean;
  name?: string;
  selected?: boolean;
  size?: number | "full";
};

export const Avatar = ({
  imgSrc = "",
  loading = false,
  name = "",
  selected = false,
  size = 24,
  children,
  ...props
}: AvatarProps) => {
  if (loading)
    return (
      <Skeleton
        css={[
          baseStyle,
          emotionDynamics.heightWidth({ height: size, width: size }),
        ]}
        {...props}
      />
    );

  return (
    <RadixUIAvatar.Root
      css={[
        baseStyle,
        selected && selectedStyle,
        emotionDynamics.heightWidth({
          height: size === "full" ? "100%" : size,
          width: size === "full" ? "100%" : size,
        }),
      ]}
      {...props}
    >
      {imgSrc &&
        (typeof imgSrc === "string" ? (
          <RadixUIAvatar.Image
            alt={name}
            src={imgSrc}
            css={[
              baseImageStyle,
              emotionDynamics.heightWidth({ height: size, width: size }),
            ]}
          />
        ) : (
          <Image
            src={imgSrc}
            alt={name}
            width={typeof size === "number" ? size : undefined}
            height={typeof size === "number" ? size : undefined}
            fill={typeof size === "string"}
            placeholder={"blur"}
            css={baseImageStyle}
          />
        ))}
      {children}
      <RadixUIAvatar.Fallback css={baseFallbackStyle} />
    </RadixUIAvatar.Root>
  );
};
